import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  HiOutlineSearch,
  HiPlus,
  HiChevronRight,
  HiOutlineClipboardList,
  HiOutlineTag,
  HiOutlineCalendar,
  HiOutlineRefresh,
  HiOutlineExclamationCircle,
  HiOutlineX,
  HiArrowLeft,
} from "react-icons/hi";
import { DefectService } from "../../../../services/DefectService";
import { StockService } from "../../../../services/StockService";

export const SpeciesBadge = ({ code, name }) => (
  <span className="inline-flex items-center max-w-full gap-2">
    {code && (
      <span className="shrink-0 rounded-md bg-tclPrimary/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-tclPrimary">
        {code}
      </span>
    )}
    <span className="truncate text-slate-700">{name || "Unknown species"}</span>
  </span>
);

const StatTile = ({ icon: Icon, label, value, tone }) => (
  <div className="flex items-center gap-4 p-3 bg-white border shadow-sm sm:p-4 rounded-2xl border-slate-200/80">
    <span className={`hidden sm:grid h-11 w-11 shrink-0 place-items-center rounded-xl ${tone}`}>
      <Icon className="w-5 h-5" />
    </span>
    <div className="min-w-0">
      <p className="text-[11px] sm:text-xs font-medium tracking-wide uppercase text-slate-500 leading-tight">{label}</p>
      <p className="mt-0.5 text-xl sm:text-2xl font-semibold tabular-nums text-slate-800">{value}</p>
    </div>
  </div>
);

const SkeletonRows = () => (
  <ul className="divide-y divide-slate-100">
    {Array.from({ length: 5 }).map((_, i) => (
      <li key={i} className="flex items-center gap-6 px-5 py-4 animate-pulse">
        <div className="w-40 h-4 rounded bg-slate-200" />
        <div className="w-24 h-4 rounded bg-slate-200" />
        <div className="w-24 h-4 rounded bg-slate-200" />
        <div className="flex-1 h-4 rounded bg-slate-100" />
      </li>
    ))}
  </ul>
);

const EmptyState = ({ icon: Icon, title, body, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
    <span className="grid mb-4 rounded-full h-14 w-14 place-items-center bg-slate-100 text-slate-400">
      <Icon className="h-7 w-7" />
    </span>
    <h3 className="text-base font-semibold text-slate-800">{title}</h3>
    <p className="max-w-sm mt-1 text-sm text-slate-500">{body}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const Defects = () => {
  const navigate = useNavigate();
  const [defects, setDefects] = useState([]);
  const [speciesById, setSpeciesById] = useState({});
  const [status, setStatus] = useState("loading");
  const [search, setSearch] = useState("");
  const [speciesFilter, setSpeciesFilter] = useState("");

  const load = () => {
    setStatus("loading");
    Promise.all([DefectService.GetAllDefects(), StockService.GetAllSpecies()]).then(
      ([defectList, speciesList]) => {
        const lookup = {};
        (speciesList || []).forEach((s) => {
          lookup[s.speciesId] = s;
        });
        setSpeciesById(lookup);
        if (defectList === null) {
          setStatus("error");
          return;
        }
        setDefects(defectList);
        setStatus("ready");
      }
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(
    () =>
      defects.map((d) => {
        const species = speciesById[d.speciesId] || {};
        return {
          ...d,
          speciesName: d.speciesName || species.speciesName,
          speciesCode: d.speciesCode || species.speciesCode,
        };
      }),
    [defects, speciesById]
  );

  const speciesOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (!seen.has(r.speciesId)) seen.set(r.speciesId, r.speciesCode || r.speciesName || `#${r.speciesId}`);
    });
    return [...seen.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (speciesFilter && String(r.speciesId) !== speciesFilter) return false;
      if (!q) return true;
      return [r.stockNumber, r.contractorNumber, r.remark, r.speciesName, r.speciesCode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search, speciesFilter]);

  const stats = useMemo(() => {
    const startOfMonth = moment().startOf("month");
    return {
      total: rows.length,
      species: new Set(rows.map((r) => r.speciesId)).size,
      thisMonth: rows.filter((r) => moment(r.dateCreated).isSameOrAfter(startOfMonth)).length,
    };
  }, [rows]);

  const hasFilters = search !== "" || speciesFilter !== "";
  const logButton = (
    <button
      type="button"
      onClick={() => navigate("add")}
      className="inline-flex items-center gap-2 rounded-xl bg-tclPrimary px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-tclPrimary/30 transition hover:bg-tclPrimary/90 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-tclPrimary/40 focus-visible:ring-offset-2"
    >
      <HiPlus className="w-4 h-4" />
      Log defect
    </button>
  );

  return (
    <div className="w-full pb-10 space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/dashboard/tree-fell/all")}
            className="inline-flex items-center gap-1.5 mb-3 text-sm font-medium text-slate-500 transition hover:text-tclPrimary"
          >
            <HiArrowLeft className="w-4 h-4" />
            Log Management
          </button>
          <p className="text-xs font-semibold tracking-widest uppercase text-tclPrimary">Log Management</p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-800">Defect Tracking</h2>
          <p className="mt-1 text-sm text-slate-500">
            Record defects found on stock, with the species, stock and contractor they belong to.
          </p>
        </div>
        {logButton}
      </header>

      <section className="grid grid-cols-3 gap-3 sm:gap-4">
        <StatTile
          icon={HiOutlineClipboardList}
          label="Defects logged"
          value={status === "ready" ? stats.total : "–"}
          tone="bg-amber-50 text-amber-600"
        />
        <StatTile
          icon={HiOutlineTag}
          label="Species affected"
          value={status === "ready" ? stats.species : "–"}
          tone="bg-tclPrimary/10 text-tclPrimary"
        />
        <StatTile
          icon={HiOutlineCalendar}
          label="This month"
          value={status === "ready" ? stats.thisMonth : "–"}
          tone="bg-sky-50 text-sky-600"
        />
      </section>

      <section className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200/80">
        <div className="flex flex-col gap-3 p-4 border-b border-slate-100 md:flex-row md:items-center">
          <label className="relative flex-1">
            <span className="sr-only">Search defects</span>
            <HiOutlineSearch className="absolute w-5 h-5 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stock no, contractor no, species or remark"
              className="w-full py-2.5 pl-10 pr-3 text-sm border outline-none rounded-xl border-slate-200 bg-slate-50/60 text-slate-700 placeholder:text-slate-400 transition focus:border-tclPrimary/60 focus:bg-white focus:ring-4 focus:ring-tclPrimary/10"
            />
          </label>
          <div className="flex items-center gap-2">
            <select
              value={speciesFilter}
              onChange={(e) => setSpeciesFilter(e.target.value)}
              aria-label="Filter by species"
              className="py-2.5 pl-3 pr-8 text-sm border outline-none rounded-xl border-slate-200 bg-slate-50/60 text-slate-700 transition focus:border-tclPrimary/60 focus:bg-white focus:ring-4 focus:ring-tclPrimary/10"
            >
              <option value="">All species</option>
              {speciesOptions.map(([id, label]) => (
                <option key={id} value={String(id)}>
                  {label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setSpeciesFilter("");
                }}
                className="inline-flex items-center gap-1 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <HiOutlineX className="w-4 h-4" />
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={load}
              title="Refresh"
              aria-label="Refresh"
              className="grid transition border h-10 w-10 place-items-center rounded-xl border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            >
              <HiOutlineRefresh className={`h-4 w-4 ${status === "loading" ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {status === "loading" && <SkeletonRows />}

        {status === "error" && (
          <EmptyState
            icon={HiOutlineExclamationCircle}
            title="Couldn't load defects"
            body="The defect list could not be fetched. Check your connection and try again."
            action={
              <button
                type="button"
                onClick={load}
                className="px-4 py-2 text-sm font-semibold transition border rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Try again
              </button>
            }
          />
        )}

        {status === "ready" && rows.length === 0 && (
          <EmptyState
            icon={HiOutlineClipboardList}
            title="No defects logged yet"
            body="When a defect is found on stock, log it here so it can be tracked back to the species and contractor."
            action={logButton}
          />
        )}

        {status === "ready" && rows.length > 0 && filtered.length === 0 && (
          <EmptyState
            icon={HiOutlineSearch}
            title="No matching defects"
            body="Nothing matches your search or species filter."
          />
        )}

        {status === "ready" && filtered.length > 0 && (
          <>
            <div className="hidden grid-cols-12 gap-4 px-5 py-3 text-xs font-semibold tracking-wide uppercase md:grid bg-slate-50/70 text-slate-500">
              <span className="col-span-3">Species</span>
              <span className="col-span-2">Stock no</span>
              <span className="col-span-2">Contractor no</span>
              <span className="col-span-3">Remark</span>
              <span className="col-span-2 text-right">Logged</span>
            </div>
            <ul className="divide-y divide-slate-100">
              {filtered.map((d) => (
                <li key={d.defectId}>
                  <button
                    type="button"
                    onClick={() => navigate(`${d.defectId}`)}
                    className="grid w-full grid-cols-1 gap-2 px-5 py-4 text-left transition group md:grid-cols-12 md:items-center md:gap-4 hover:bg-tclPrimary/[0.03] focus:outline-none focus-visible:bg-tclPrimary/5"
                  >
                    <span className="min-w-0 md:col-span-3">
                      <SpeciesBadge code={d.speciesCode} name={d.speciesName} />
                    </span>
                    <span className="text-sm md:col-span-2">
                      <span className="mr-2 text-xs text-slate-400 md:hidden">Stock no</span>
                      <span className="font-mono text-slate-800">{d.stockNumber}</span>
                    </span>
                    <span className="text-sm md:col-span-2">
                      <span className="mr-2 text-xs text-slate-400 md:hidden">Contractor no</span>
                      <span className="font-mono text-slate-800">{d.contractorNumber}</span>
                    </span>
                    <span className="text-sm truncate md:col-span-3 text-slate-500" title={d.remark}>
                      {d.remark || <span className="italic text-slate-300">No remark</span>}
                    </span>
                    <span className="flex items-center justify-between gap-2 text-sm md:col-span-2 md:justify-end text-slate-500">
                      <span title={moment(d.dateCreated).format("DD MMM YYYY, HH:mm")}>
                        {moment(d.dateCreated).format("DD MMM YYYY")}
                      </span>
                      <HiChevronRight className="w-4 h-4 transition text-slate-300 group-hover:translate-x-0.5 group-hover:text-tclPrimary" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-5 py-3 text-xs border-t border-slate-100 text-slate-400">
              Showing {filtered.length} of {rows.length} defect{rows.length === 1 ? "" : "s"}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default Defects;
